import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Map as MapIcon } from 'lucide-react';
import { ROUTES } from '../../lib/routes';
import { ProductCard } from '../layout/ProductCard';

// Dummy product data with coordinates
const featuredProducts = [
  {
    id: 1,
    name: 'Organic Fresh Tomatoes',
    price: 4.99,
    image: 'https://images.unsplash.com/photo-1546095668-5c0d0a0b0b0b?w=400&h=400&fit=crop',
    category: 'Vegetables',
    rating: 4.8,
    reviewCount: 24,
    origin: 'Kathmandu Valley',
    lat: 27.7172,
    lng: 85.3240,
    isOrganic: true,
    isLatest: false,
  },
  {
    id: 2,
    name: 'Premium Honey',
    price: 12.99,
    image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=400&fit=crop',
    category: 'Dairy & Honey',
    rating: 4.9,
    reviewCount: 18,
    origin: 'Pokhara',
    lat: 28.2096,
    lng: 83.9856,
    isOrganic: true,
    isLatest: true,
  },
  {
    id: 3,
    name: 'Fresh Organic Eggs',
    price: 6.99,
    image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400&h=400&fit=crop',
    category: 'Dairy & Honey',
    rating: 4.7,
    reviewCount: 32,
    origin: 'Chitwan',
    lat: 27.5294,
    lng: 84.3542,
    isOrganic: true,
    isLatest: false,
  },
  {
    id: 4,
    name: 'Organic Carrots',
    price: 3.99,
    image: 'https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?w=400&h=400&fit=crop',
    category: 'Vegetables',
    rating: 4.6,
    reviewCount: 15,
    origin: 'Mustang',
    lat: 28.8333,
    lng: 83.8333,
    isOrganic: true,
    isLatest: false,
  },
  {
    id: 5,
    name: 'Organic Rice',
    price: 8.99,
    image: 'https://images.pexels.com/photos/1306548/pexels-photo-1306548.jpeg',
    category: 'Grains',
    rating: 4.8,
    reviewCount: 28,
    origin: 'Janakpur',
    lat: 26.7288,
    lng: 85.9254,
    isOrganic: true,
    isLatest: false,
  },
  {
    id: 6,
    name: 'Fresh Spinach',
    price: 2.99,
    image: 'https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=400&h=400&fit=crop',
    category: 'Vegetables',
    rating: 4.5,
    reviewCount: 12,
    origin: 'Lalitpur',
    lat: 27.6667,
    lng: 85.3333,
    isOrganic: true,
    isLatest: true,
  },
];

// Separate component for animated product cards
const ProductCardWithAnimation: React.FC<{ product: typeof featuredProducts[0]; index: number }> = ({ product, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{
        transitionDelay: `${index * 100}ms`, // Staggered animation
      }}
    >
      <ProductCard
        id={product.id}
        name={product.name}
        price={product.price}
        image={product.image}
        isLatest={product.isLatest}
        origin={product.origin}
        rating={product.rating}
        reviewCount={product.reviewCount}
        category={product.category}
        isOrganic={product.isOrganic}
      />
    </div>
  );
};

export const FeaturedProducts: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<maplibregl.Map | null>(null);
  const [showMap, setShowMap] = useState(false);
  const { ref: sectionRef, inView: sectionInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (!showMap || !mapContainer.current || mapInstance.current) return;

    // Initialize map
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/streets/style.json?key=zSKe702Zq1told0U6bDZ`,
      center: [84.1240, 28.3949], // Center of Nepal
      zoom: 6,
      attributionControl: false,
    });

    // Add markers for all products
    featuredProducts.forEach((product) => {
      // Create custom marker HTML
      const markerEl = document.createElement('div');
      markerEl.className = 'custom-marker';
      markerEl.style.cssText = `
        width: 32px;
        height: 32px;
        background-color: #2E7D32;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(46, 125, 50, 0.4);
        cursor: pointer;
        transition: all 0.2s ease;
      `;

      markerEl.addEventListener('mouseenter', () => {
        markerEl.style.transform = 'scale(1.2)';
        markerEl.style.backgroundColor = '#4CAF50';
      });

      markerEl.addEventListener('mouseleave', () => {
        markerEl.style.transform = 'scale(1)';
        markerEl.style.backgroundColor = '#2E7D32';
      });

      // Create popup
      const popup = new maplibregl.Popup({
        offset: 25,
        closeButton: false,
        className: 'farmero-popup',
      }).setHTML(`
        <div style="padding: 8px;">
          <div style="font-weight: 600; color: #2E7D32; margin-bottom: 4px;">${product.name}</div>
          <div style="font-size: 12px; color: #717171; margin-bottom: 4px;">${product.origin}</div>
          <div style="font-weight: 700; color: #4CAF50;">Rs ${product.price.toFixed(2)}</div>
        </div>
      `);

      new maplibregl.Marker({ element: markerEl })
        .setLngLat([product.lng, product.lat])
        .setPopup(popup)
        .addTo(map);
    });

    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, [showMap]);

  return (
    <section className="bg-gradient-to-b from-[#E8F5E9] to-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          ref={sectionRef}
          className={`text-center mb-12 transition-all duration-700 ${
            sectionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2E7D32]/10 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-[#2E7D32]" />
            <span className="text-sm font-semibold text-[#2E7D32]">Featured Products</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-[#2E7D32] mb-4 font-poppins">
            Explore Our Fresh Organic Products
          </h2>

          <p className="text-lg text-[#2E7D32]/70 max-w-2xl mx-auto font-inter">
            Hand-picked from organic farms across Nepal, delivered fresh to your doorstep
          </p>

          {/* Map Toggle Button */}
          <button
            onClick={() => setShowMap(!showMap)}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#2E7D32] border-2 border-[#2E7D32] rounded-full font-semibold text-sm hover:bg-[#2E7D32] hover:text-white transform hover:scale-105 transition-all duration-300 shadow-md"
          >
            <MapIcon className="w-4 h-4" />
            {showMap ? 'Hide' : 'Show'} Farm Locations
          </button>
        </div>

        {/* Map Section - Collapsible */}
        {showMap && (
          <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl border-4 border-[#2E7D32]/20 animate-slide-up">
            <div
              ref={mapContainer}
              className="w-full h-[500px]"
            />
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {featuredProducts.map((product, index) => (
            <ProductCardWithAnimation
              key={product.id}
              product={product}
              index={index}
            />
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link
            to={ROUTES.PRODUCTS}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#2E7D32] text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:bg-[#4CAF50] transform hover:scale-105 transition-all duration-300"
          >
            View All Products
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

